import { useState } from "react";

import { Loader2, Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import {
  useBillingItems,
  useCreateGroup,
  useDeleteGroup,
  useGroups,
  usePackages,
  useRefreshContracts,
  useUpdateGroup,
  type ContractCacheInfo,
  type GroupItem,
  type ProductGroupConfig,
} from "../api";
import { BAR_CLASS, pillColor } from "../lib";

const COLOR_OPTIONS = ["brand", "power", "purple", "success", "orange", "teal"] as const;

interface EditorState {
  id: string | null; // null = creating
  label: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  items: GroupItem[];
}

function editorFromGroup(g: ProductGroupConfig | null): EditorState {
  return g
    ? {
        id: g.id,
        label: g.label,
        color: g.color,
        sort_order: g.sort_order,
        is_active: g.is_active,
        items: [...g.items],
      }
    : { id: null, label: "", color: "brand", sort_order: 0, is_active: true, items: [] };
}

/**
 * Admin tab: define the tracked product groups. Each group = a set of Cinch
 * billing items (picked from the live catalog); packages are a shortcut that
 * expands to their component items on pick. Groups become the dashboard's KPI
 * cards + table columns, so adding a new product here makes it trackable
 * without a deploy.
 */
export function GroupConfigTab({
  contractCache,
}: {
  contractCache: ContractCacheInfo | null;
}) {
  const groups = useGroups();
  const create = useCreateGroup();
  const update = useUpdateGroup();
  const remove = useDeleteGroup();
  const refresh = useRefreshContracts();

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<ProductGroupConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!editor) return;
    setError(null);
    const payload = {
      label: editor.label.trim(),
      color: editor.color,
      sort_order: editor.sort_order,
      is_active: editor.is_active,
      items: editor.items,
    };
    try {
      if (editor.id) await update.mutateAsync({ id: editor.id, payload });
      else await create.mutateAsync(payload);
      setEditor(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Tracked product groups</CardTitle>
            <CardDescription>
              Every active group is a KPI card and a table column on the dashboard.
              A customer is "attached" when any of the group's billing items is on
              their invoice ($) or an active contract (✓).
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setEditor(editorFromGroup(null))}>
            <Plus className="h-4 w-4" /> New group
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col divide-y divide-border">
          {groups.isLoading && (
            <p className="py-3 text-sm text-muted-foreground">Loading…</p>
          )}
          {(groups.data?.groups ?? []).map((g) => (
            <div key={g.id} className="flex items-center gap-3 py-2.5">
              <span className={cn("h-3 w-3 shrink-0 rounded-sm", BAR_CLASS[pillColor(g.color)])} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{g.label}</span>
                  <span className="text-xs text-muted-foreground">({g.key})</span>
                  {!g.is_active && <Badge variant="neutral">inactive</Badge>}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {g.items.length ? g.items.map((i) => i.item_name).join(" · ") : "No items yet"}
                </div>
              </div>
              {g.updated_by_name && (
                <span className="hidden text-xs text-muted-foreground md:inline">
                  by {g.updated_by_name}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditor(editorFromGroup(g))}
                aria-label={`Edit ${g.label}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setDeleting(g)}
                aria-label={`Delete ${g.label}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Contract-item cache</CardTitle>
            <CardDescription>
              The ✓ layer — which billing items sit on each account's active
              contracts, snapshotted from the Cinch API so every viewer sees it.
              {contractCache?.last_synced_at
                ? ` Last synced ${new Date(contractCache.last_synced_at).toLocaleString()}${
                    contractCache.status && contractCache.status !== "ok"
                      ? ` (${contractCache.status}: ${contractCache.detail ?? ""})`
                      : ""
                  }.`
                : " Never synced yet."}
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh from Cinch
          </Button>
        </CardHeader>
        {refresh.error && (
          <CardContent className="pt-0 text-sm text-destructive">
            {refresh.error instanceof ApiError ? refresh.error.message : String(refresh.error)}
          </CardContent>
        )}
        {refresh.data && (
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Synced {refresh.data.rows} contract-item rows ({refresh.data.status}).
          </CardContent>
        )}
      </Card>

      {editor && (
        <GroupEditorDialog
          editor={editor}
          setEditor={setEditor}
          onSave={save}
          saving={create.isPending || update.isPending}
          error={error}
        />
      )}

      <Dialog open={deleting != null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete "{deleting?.label}"?</DialogTitle>
            <DialogDescription>
              The dashboard loses this KPI card and column immediately. Historical
              billing data is untouched — re-creating the group restores the view.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={async () => {
                if (deleting) await remove.mutateAsync(deleting.id);
                setDeleting(null);
              }}
            >
              Delete group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupEditorDialog({
  editor,
  setEditor,
  onSave,
  saving,
  error,
}: {
  editor: EditorState;
  setEditor: (e: EditorState | null) => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}) {
  const items = useBillingItems(true);
  const packages = usePackages(true);

  const itemOptions: ComboOption[] = (items.data ?? [])
    .filter((i) => i.name && !i.is_archived)
    .map((i) => ({
      id: i.id,
      label: i.name!,
      group: "Billing items",
      badge: i.category ?? undefined,
    }));
  const packageOptions: ComboOption[] = (packages.data ?? [])
    .filter((p) => p.name)
    .map((p) => ({
      id: p.id,
      label: p.name!,
      group: "Packages",
      badge: `${p.package_items?.length ?? 0} items`,
    }));

  function addItem(id: string | null) {
    if (!id) return;
    const row = (items.data ?? []).find((i) => i.id === id);
    if (!row?.name) return;
    if (editor.items.some((i) => i.item_name === row.name)) return;
    setEditor({
      ...editor,
      items: [...editor.items, { cinch_item_id: row.id, item_name: row.name }],
    });
  }

  /** Packages funnel down to billing items: picking one appends its component
   *  items (deduped) — the package itself is never stored. */
  function addPackage(id: string | null) {
    if (!id) return;
    const pkg = (packages.data ?? []).find((p) => p.id === id);
    if (!pkg) return;
    const byId = new Map((items.data ?? []).map((i) => [i.id, i]));
    const additions: GroupItem[] = [];
    for (const pi of pkg.package_items ?? []) {
      if (!pi.item) continue;
      const name = byId.get(pi.item)?.name ?? pi.item_detail?.name;
      if (!name) continue;
      if (editor.items.some((i) => i.item_name === name)) continue;
      if (additions.some((i) => i.item_name === name)) continue;
      additions.push({ cinch_item_id: pi.item, item_name: name });
    }
    if (additions.length) setEditor({ ...editor, items: [...editor.items, ...additions] });
  }

  const catalogError =
    items.error instanceof ApiError && items.error.status === 403
      ? "The live billing-item catalog needs Cinch billing access — item names can still be typed via an existing group."
      : items.error
        ? String(items.error)
        : null;

  return (
    <Dialog open onOpenChange={(o) => !o && setEditor(null)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editor.id ? "Edit product group" : "New product group"}</DialogTitle>
          <DialogDescription>
            Pick the billing items that mean a customer "has" this product.
            Item names are matched against invoice lines in the revenue waterfall.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <Label htmlFor="group-label">Label</Label>
            <Input
              id="group-label"
              value={editor.label}
              onChange={(e) => setEditor({ ...editor, label: e.target.value })}
              placeholder="e.g. Reputation"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Color</Label>
            <Select
              value={editor.color}
              onValueChange={(v) => setEditor({ ...editor, color: v })}
            >
              <SelectTrigger className="h-9 w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLOR_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    <span className="flex items-center gap-2">
                      <span className={cn("h-2.5 w-2.5 rounded-sm", BAR_CLASS[c])} />
                      {c}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="group-order">Order</Label>
            <Input
              id="group-order"
              type="number"
              className="w-20"
              value={editor.sort_order}
              onChange={(e) => setEditor({ ...editor, sort_order: Number(e.target.value) })}
            />
          </div>
          <label className="flex h-9 items-center gap-2 text-sm">
            <Checkbox
              checked={editor.is_active}
              onCheckedChange={(v) => setEditor({ ...editor, is_active: v === true })}
            />
            Active
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Billing items ({editor.items.length})</Label>
          {editor.items.length > 0 && (
            <ul className="flex max-h-[160px] flex-col gap-1 overflow-auto">
              {editor.items.map((item) => (
                <li
                  key={item.item_name}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1 text-sm"
                >
                  <span className="truncate">{item.item_name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      setEditor({
                        ...editor,
                        items: editor.items.filter((i) => i.item_name !== item.item_name),
                      })
                    }
                    aria-label={`Remove ${item.item_name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {catalogError && <p className="text-xs text-warning">{catalogError}</p>}
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[220px] flex-1">
              <Combobox
                options={itemOptions}
                value={null}
                onChange={addItem}
                placeholder={items.isLoading ? "Loading catalog…" : "Add a billing item…"}
                searchPlaceholder="Search items…"
                emptyText="No items found."
              />
            </div>
            <div className="min-w-[220px] flex-1">
              <Combobox
                options={packageOptions}
                value={null}
                onChange={addPackage}
                placeholder="Add a package's items…"
                searchPlaceholder="Search packages…"
                emptyText="No packages found."
              />
            </div>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => setEditor(null)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving || !editor.label.trim()}>
            {saving ? "Saving…" : editor.id ? "Save changes" : "Create group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
